import React from "react";
import { Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";

import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { Tabs, TabProps } from "@components/Tabs";
import { makeStyles } from "@material-ui/core/styles";
import { ErrorBoundary, withErrorBoundary } from "@components/error-boundary";

type Props = {
  id: string;
  main: React.ReactNode;
  topbar: React.ReactNode;
  hospitalityTab: () => React.ReactNode;
  financeTab: () => React.ReactNode;
  eventsTab: () => React.ReactNode;
  filesTab: () => React.ReactNode;
  timeReportTab: () => React.ReactNode;
  timeReportKitTab: () => React.ReactNode;
  bookingKitAutomationTab: () => React.ReactNode;
  alerts: () => React.ReactNode;
  bookingKitCreateWfStatus: () => React.ReactNode;
  footer: React.ReactNode;
  week: string;
  currentTab: string;
  tabOnChange: (tab: string) => void;
};

export const BookingProfileLayout = (p: Props) => {
  const { id, currentTab, tabOnChange, ...props } = p;
  const classes = useStyles({});

  const tabs: TabProps[] = [
    {
      fitted: true,
      label: locale.Finance,
      value: "finance",
      content: withErrorBoundary(props.financeTab())
    },
    {
      fitted: true,
      label: locale.AccommodationTravel,
      value: "hospitality",
      content: withErrorBoundary(props.hospitalityTab())
    },
    {
      fitted: true,
      label: locale.TimeReport,
      value: "timereport",
      content: withErrorBoundary(props.timeReportTab())
    },
    {
      fitted: true,
      label: locale.ActionLog,
      value: "events",
      content: withErrorBoundary(props.eventsTab())
    },
    {
      fitted: false,
      label: locale.Files,
      value: "files",
      content: withErrorBoundary(props.filesTab())
    },
    {
      fitted: true,
      label: locale.Kit,
      value: "kit",
      content: withErrorBoundary(props.timeReportKitTab())
    },
    {
      fitted: true,
      label: locale.KitAutomation,
      value: "kitAutomation",
      content: withErrorBoundary(props.bookingKitAutomationTab())
    }
  ];

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        {withErrorBoundary(props.topbar)}
      </Grid>

      <Grid item xs={12} className={classes.main}>
        {withErrorBoundary(props.main)}
      </Grid>

      <Grid item xs={12}>
        <ErrorBoundary>
          <Tabs
            tabs={tabs}
            TabsProps={{
              indicatorColor: "primary"
            }}
            key={props.week}
            value={currentTab}
            className={classes.tabs}
            AppBarProps={{ className: classes.tabsAppbar }}
            onChange={(_, tab) => tabOnChange(tab)}
          >
            {({ header, page }) => (
              <div>
                <div className={classes.tabWrapper}>
                  <div className={classes.tabHeader}>
                    <Typography style={{ fontWeight: "bold" }}>
                      {locale.Week} {props.week}
                    </Typography>
                  </div>
                  {header}
                </div>

                {props.bookingKitCreateWfStatus()}
                {props.alerts()}
                {page}
              </div>
            )}
          </Tabs>
        </ErrorBoundary>
      </Grid>
      <Grid item xs={12} style={{ marginTop: -16 }}>
        {withErrorBoundary(props.footer)}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    padding: theme.spacing(2),
    marginTop: -16
  },

  main: {
    "& > div:not(:last-child)": {
      marginBottom: theme.spacing(2)
    }
  },

  tabWrapper: {
    display: "flex",
    alignItems: "stretch"
  },

  tabHeader: {
    alignItems: "center",
    background: theme.palette.secondary.main,
    borderTopLeftRadius: 4,
    display: "flex",
    flex: 0,
    justifyContent: "center",
    padding: "0 16px",
    whiteSpace: "nowrap",

    "& > p": {
      color: theme.palette.secondary.contrast
    }
  },

  tabs: {},

  tabsAppbar: {
    borderTopRightRadius: 4,
    boxShadow: "none",
    backgroundColor: theme.palette.grey[300],
    flex: 1,

    "& span": {
      color: theme.palette.text.primary
    }
  }
}));

export default BookingProfileLayout;
